import React, { useEffect, useState, useRef, forwardRef } from "react";
import { useDetail } from "seed/gql";
import { usePost } from "seed/api";
import { Loading } from "seed/helpers";
import { CHAT } from "seed/gql/queries";
import Conversation from "./conversation/Conversation";
import History from "./history/History";
import Rules from "./rules/Rules";
import { clearChatState, getChatStateKey, readChatState, writeChatState } from "./Chat.lib.keepState";
import View from "./Chat.view";

const AGENT_SECTION = "agent";

const ensureExecutionSessionId = () => {
  const stored = sessionStorage.getItem("execution_session_id");
  if (stored) return stored;
  const sessionId =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  sessionStorage.setItem("execution_session_id", sessionId);
  return sessionId;
};

const Chat = forwardRef(({
  autoRunNodeId, // If provided, this node will be executed on chat load (if not already executed)
  autoRunProcessName, // Like autoRunNodeId, but naming the process; resolves in this flow or any it inherits from
  currentView, // The current project-view object, used to determine if the chat is being rendered in the agent section
  executionVariables, // If provided, these variables will be used when executing a node as input parameters
  chat: providedChat, // If provided, this chat will be used instead of fetching one from the server
  chatId, // If provided, this chat will be fetched from the server instead of using a provided chat
  flow, // If provided, this flow will be used to initialize a new chat if no chat is provided or fetched
  freshChat, // Opens a new conversation on every load instead of rejoining the one this slot was left on
  functions, // Forwarded: functions a running process can call by name through the chat
  isExecutionChat, // The chat only shows an execution's transcript: no history, and the initial process never runs on it
  isReadOnly, // Forwarded: renders the thread without the composer
  keepState, // The slot remembers its chat across reloads instead of only for the browser session. A string is a token: change it and the slot starts a new conversation
  showScreenshotAttachment, // Forwarded: adds the screen-capture option to the composer's clip
  slotId, // Forwarded: identifies this chat's slot when it is portaled into a custom view
  onExecutionMessage, // Forwarded: notifies the caller of execution status changes
}, ref) => {

  const [chat, setChat] = useState(providedChat || null);

  // The conversation the user picked out of the history, and whether that list is open.
  // A picked chat outranks every other way of resolving one: it is the only one the user
  // asked for explicitly.
  const [historyChatId, setHistoryChatId] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // The chat header's dropdown paints one panel at a time: opening one closes the other.
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  // The chat a previous page load left on this slot, when keepState is on. It is used
  // exactly like the chatId prop — fetched, never created — so the reload rejoins the
  // thread rather than opening a second one beside it.
  const [rememberedChatId, setRememberedChatId] = useState(null);

  // Remembers which (chat, node) pair already auto-ran, preventing re-running when the chat refreshes
  const autoRunKeyRef = useRef(null);

  const stateKey = keepState ? getChatStateKey(flow?.id, slotId) : null;
  const stateToken = typeof keepState == "string" ? keepState : null;
  const resolvedChatId = historyChatId || chatId || rememberedChatId;

  const reqChat = useDetail(CHAT, resolvedChatId || 0);
  const [callInitChat] = usePost("/chats/init_web_chat", { onCompleted: setChat });
  const [callExecuteNode] = usePost("/chats/create_execution_message");

  // Initialize a new chat if we have a flow but no chat yet. This is the case when
  useEffect(() => {

    if (!flow || providedChat) return;

    // A slot that opens a new conversation every time: reloading the page is how the
    // user starts over, so nothing from the previous load is resumed and the chat is
    // asked for outright rather than rejoined. `force_new_chat` is what makes it a new
    // one — without it the server hands back the chat this browser session already has.
    if (freshChat && !chatId) {
      setChat(null);
      callInitChat({
        flow_id: flow.id,
        execution_session_id: ensureExecutionSessionId(),
        force_new_chat: true,
      });
      return;
    }

    // What a previous load left on this slot: the conversation it resolved to, and the
    // auto-run that already happened on it. The key embeds the chat id, so restoring it
    // for a different chat is harmless — it simply will not match.
    const remembered = readChatState(stateKey, stateToken);
    if (remembered?.autoRunKey) autoRunKeyRef.current = remembered.autoRunKey;

    if (chatId) return;
    if (remembered) {
      setRememberedChatId(remembered.chatId);
      return;
    }

    setChat(null);
    callInitChat({
      flow_id: flow.id,
      execution_session_id: ensureExecutionSessionId(),
    });

  }, [flow?.id, providedChat, chatId, freshChat, stateKey, stateToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!resolvedChatId || !reqChat.data?.chat) return;
    setChat(reqChat.data.chat);
  }, [resolvedChatId, reqChat.data]);

  // The remembered chat can be gone — deleted, or another user's after a logout. Without
  // this the slot would sit on the spinner forever, so forget it and open the conversation
  // it would have opened on a first visit.
  useEffect(() => {

    const isMissing = reqChat.error || (reqChat.data != null && !reqChat.data.chat);
    if (!rememberedChatId || reqChat.loading || !isMissing) return;

    clearChatState(stateKey);
    setRememberedChatId(null);
    callInitChat({
      flow_id: flow.id,
      execution_session_id: ensureExecutionSessionId(),
    });

  }, [rememberedChatId, reqChat.loading, reqChat.error, reqChat.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remember the conversation as soon as it resolves, so the next load finds it.
  useEffect(() => {
    if (!stateKey || !chat?.id) return;
    writeChatState(stateKey, stateToken, { chatId: chat.id });
  }, [stateKey, stateToken, chat?.id]);

  // Run process on start based on next priority rules:
  // 1. If autoRunProcessName is provided, run the process with that name. It is resolved
  //    server-side across the flow's inheritance chain, so it can live in a parent flow.
  // 2. If autoRunNodeId is provided, run that node.
  // 3. If neither is provided, but the chat has run_on_start enabled, run the initial_node_id.
  //    Not over an execution's transcript: that chat shows a run, it does not start one.
  useEffect(() => {

    if (!chat)
      return;

    const hasAutoRunProcessName = autoRunProcessName != null;
    const hasAutoRunNodeId = autoRunNodeId != null;
    const hasRunOnStart = !isExecutionChat && Boolean(chat.settings.run_on_start && chat.settings.initial_node_id);
    if (!hasAutoRunProcessName && !hasAutoRunNodeId && !hasRunOnStart) return;

    let nodeId = null;
    if (!hasAutoRunProcessName) {
      if (hasAutoRunNodeId) nodeId = autoRunNodeId;
      else if (hasRunOnStart) nodeId = chat.settings.initial_node_id;
    }

    const autoRunKey = `${chat.id}:${autoRunProcessName ?? nodeId}`;
    if (autoRunKeyRef.current == autoRunKey) return;
    autoRunKeyRef.current = autoRunKey;
    // Stored next to the chat id: on the next load it is what stops the process from
    // running a second time over the same conversation. No-ops without keepState.
    writeChatState(stateKey, stateToken, { chatId: chat.id, autoRunKey });

    callExecuteNode({
      ...(hasAutoRunProcessName ? { node_name: autoRunProcessName } : { node_id: nodeId }),
      chat_id: chat.id,
      session_id: ensureExecutionSessionId(),
      variables: executionVariables || {},
    });

  }, [chat, autoRunNodeId, autoRunProcessName]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new conversation replaces the remembered one: keeping it would send the next load
  // back to the thread the user just walked away from.
  const onClickNewChat = () => {
    clearChatState(stateKey);
    setRememberedChatId(null);
    setHistoryChatId(null);
    autoRunKeyRef.current = null;
    callInitChat({
      flow_id: flow.id,
      execution_session_id: ensureExecutionSessionId(),
      force_new_chat: true,
    });
  }

  // Coming back to a past conversation is not starting the slot's process again: the run
  // the user came back for already happened in it, so the auto-run is marked as done for
  // the chat being opened before it can fire a second one on top of it.
  const onSelectHistoryChat = (selectedChatId) => {
    setIsHistoryOpen(false);
    if (selectedChatId == chat?.id) return;
    autoRunKeyRef.current = `${selectedChatId}:${autoRunProcessName ?? autoRunNodeId}`;
    setChat(null);
    setRememberedChatId(null);
    setHistoryChatId(selectedChatId);
  }

  // Only a writable chat of a known flow has a history to offer: an execution transcript
  // and a read-only thread are each showing one specific conversation on purpose.
  const isHistoryEnabled = Boolean(flow?.id) && !isReadOnly && !isExecutionChat;

  const historyComponent = isHistoryOpen && (
    <History
      currentChatId={chat?.id}
      flow={flow}
      nodeId={autoRunNodeId}
      processName={autoRunProcessName}
      onClose={() => setIsHistoryOpen(false)}
      onSelectChat={onSelectHistoryChat}
    />
  );

  const rulesComponent = isRulesOpen && (
    <Rules
      flow={flow}
      nodeId={autoRunNodeId}
      onClose={() => setIsRulesOpen(false)}
    />
  );

  // Keyed by conversation: the thread, its cursor and its websockets all belong to one
  // chat, and switching to another one has to start them over rather than merge into it.
  const conversationComponent = !chat ? <Loading /> : (
    <Conversation
      key={chat.id}
      ref={ref}
      chat={chat}
      executionVariables={executionVariables}
      flow={flow}
      functions={functions}
      historyComponent={historyComponent}
      isExecutionChat={isExecutionChat}
      isHistoryEnabled={isHistoryEnabled}
      isReadOnly={isReadOnly}
      rulesComponent={rulesComponent}
      showScreenshotAttachment={showScreenshotAttachment}
      slotId={slotId}
      onClickNewChat={onClickNewChat}
      onClickToggleHistory={() => {
        setIsRulesOpen(false);
        setIsHistoryOpen(!isHistoryOpen);
      }}
      onClickToggleRules={() => {
        setIsHistoryOpen(false);
        setIsRulesOpen(!isRulesOpen);
      }}
      onExecutionMessage={onExecutionMessage}
    />
  );

  return <View
    isAgentSection={currentView?.name == AGENT_SECTION}
    conversationComponent={conversationComponent}
  />;

});

Chat.displayName = "Chat";

export default Chat;